import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Phone, Mail, Download, Smartphone } from 'lucide-react';
import { ACCENTS } from './theme';

export const SpecsSection = ({ product }) => {
  const { specs, accent, testid } = product;
  const t = ACCENTS[accent];
  const [activeSpec, setActiveSpec] = useState(specs.defaultTab);
  return (
    <section className="py-20 px-6 lg:px-12 bg-gray-900 text-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className={`${t.labelDark} font-semibold uppercase tracking-wide text-sm`}>Specifications</span>
          <h2 className="text-3xl lg:text-4xl font-bold mt-2 mb-4">Technical Specifications</h2>
        </div>

        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {specs.tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSpec(tab.id)}
              data-testid={`${testid}-spec-tab-${tab.id}`}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                activeSpec === tab.id ? `${t.specActive} text-white` : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {specs.data[activeSpec]?.map((spec) => (
            <div key={spec.label} className="bg-white/5 backdrop-blur p-6 rounded-xl border border-white/10">
              <p className="text-gray-400 text-sm mb-1">{spec.label}</p>
              <p className={`text-xl font-bold ${t.specValue}`}>{spec.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const ContactSection = ({ product }) => {
  const { contact, brochureUrl, accent } = product;
  const t = ACCENTS[accent];
  return (
    <section id="contact" className={`py-20 px-6 lg:px-12 bg-gradient-to-br ${contact.gradient} text-white`}>
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl lg:text-4xl font-bold mb-6">{contact.heading}</h2>
        <p className={`text-xl ${t.contactText} mb-8`}>{contact.sub}</p>

        <div className="bg-white/10 backdrop-blur rounded-2xl p-8 mb-8">
          <div className="grid md:grid-cols-3 gap-6">
            <a href="tel:+917011370322" className="flex flex-col items-center p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-all">
              <Phone className="w-8 h-8 mb-2" />
              <p className="font-semibold">Enquiries</p>
              <p className={t.contactSub}>+91-7011370322</p>
            </a>
            <a href="https://wa.me/917011370322" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-all">
              <Smartphone className="w-8 h-8 mb-2" />
              <p className="font-semibold">WhatsApp</p>
              <p className={t.contactSub}>+91-7011370322</p>
            </a>
            <a href="mailto:sales@harpalyk.com" className="flex flex-col items-center p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-all">
              <Mail className="w-8 h-8 mb-2" />
              <p className="font-semibold">Email</p>
              <p className={t.contactSub}>sales@harpalyk.com</p>
            </a>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-4">
          <a href={brochureUrl} target="_blank" rel="noopener noreferrer" className={`bg-white ${t.contactBtn} px-8 py-4 rounded-lg font-bold transition-all flex items-center gap-2`}>
            <Download className="w-5 h-5" /> Download Brochure
          </a>
          <a href="/#contact" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white/10 transition-all">
            Request Demo
          </a>
        </div>
      </div>
    </section>
  );
};

export const FaqSection = ({ product }) => {
  const { faqs, accent, testid } = product;
  const t = ACCENTS[accent];
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);
  return (
    <section className="py-20 px-6 lg:px-12">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <span className={`${t.label} font-semibold uppercase tracking-wide text-sm`}>FAQs</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">Frequently Asked Questions</h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={faq.question} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <button onClick={() => toggleFaq(index)} data-testid={`${testid}-faq-${index}`} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-all">
                <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                {openFaq === index ? (
                  <ChevronUp className={`w-5 h-5 ${t.chevron} flex-shrink-0`} />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                )}
              </button>
              {openFaq === index && (
                <div className="px-6 pb-5">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const RelatedLinks = ({ product }) => {
  const t = ACCENTS[product.accent];
  return (
    <section className="py-12 px-6 lg:px-12 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Explore More Harpalyk Products</h3>
        <div className="flex flex-wrap gap-4">
          {product.related.map((link) => (
            <a key={link.href} href={link.href} className={`${t.link} underline`}>{link.label}</a>
          ))}
        </div>
      </div>
    </section>
  );
};
